import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getShipmentDocuments, getDocumentTypes, uploadDocument, downloadDocument, deleteDocument, updateDocument } from '../lib/api';
import { toast } from 'sonner';
import { Upload, Download, Trash2, Eye, EyeOff, FileText, File, FileSpreadsheet, Loader2, FolderOpen, Ship, Package, Shield, DollarSign, Truck } from 'lucide-react';

const CATEGORY_ICONS = {
  transporte: Truck,
  comercial: FileSpreadsheet,
  aduanas: Shield,
  regulatorio: Shield,
  financiero: DollarSign,
  otro: File,
};

const CATEGORY_COLORS = {
  transporte: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
  comercial: 'text-green-400 bg-green-500/10 border-green-500/30',
  aduanas: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  regulatorio: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
  financiero: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  otro: 'text-gray-400 bg-gray-500/10 border-gray-500/30',
};

export function DocumentsPanel({ shipmentMbl, containers = [], isAdmin = false }) {
  const [documents, setDocuments] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [uploadVisible, setUploadVisible] = useState(true);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [docsRes, typesRes] = await Promise.all([
        getShipmentDocuments(shipmentMbl),
        getDocumentTypes(),
      ]);
      setDocuments(docsRes.data);
      setDocTypes(typesRes.data);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  }, [shipmentMbl]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadType) {
      toast.error('Selecciona un tipo de documento');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await uploadDocument(formData, {
        shipment_mbl: shipmentMbl,
        doc_type: uploadType,
        visible_to_client: uploadVisible,
      });
      toast.success('Documento subido');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await downloadDocument(doc.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error al descargar');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Eliminar este documento?')) return;
    try {
      await deleteDocument(docId);
      toast.success('Documento eliminado');
      loadData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggleVisibility = async (doc) => {
    try {
      await updateDocument(doc.id, { visible_to_client: !doc.visible_to_client });
      toast.success(doc.visible_to_client ? 'Oculto para cliente' : 'Visible para cliente');
      loadData();
    } catch (err) {
      toast.error('Error');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Group by category for timeline
  const grouped = {};
  documents.forEach(doc => {
    const cat = doc.category || 'otro';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(doc);
  });

  return (
    <div className="space-y-4" data-testid="documents-panel">
      {/* Upload Section */}
      {isAdmin && (
        <div className="bg-muted/30 rounded-lg p-4 border border-dashed border-border">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-48">
              <Label className="text-xs text-muted-foreground mb-1 block">Tipo de Documento</Label>
              <Select value={uploadType || '_select'} onValueChange={(v) => setUploadType(v === '_select' ? '' : v)}>
                <SelectTrigger data-testid="doc-type-select">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_select">Seleccionar tipo...</SelectItem>
                  {docTypes.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Archivo</Label>
              <Input
                ref={fileInputRef}
                type="file"
                onChange={handleUpload}
                disabled={uploading || !uploadType}
                className="text-sm"
                data-testid="doc-file-input"
              />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <Switch checked={uploadVisible} onCheckedChange={setUploadVisible} />
              {uploading && <Loader2 size={16} className="animate-spin text-primary" />}
            </div>
          </div>
        </div>
      )}

      {/* Documents Timeline */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-primary" size={24} />
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FolderOpen size={32} className="mx-auto mb-2 opacity-30" />
          <p>No hay documentos para este embarque</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([category, docs]) => {
            const CatIcon = CATEGORY_ICONS[category] || File;
            const colorClass = CATEGORY_COLORS[category] || CATEGORY_COLORS.otro;
            const categoryLabel = {
              transporte: 'Transporte',
              comercial: 'Comercial',
              aduanas: 'Aduanas',
              regulatorio: 'Regulatorio',
              financiero: 'Financiero',
              otro: 'Otros',
            }[category] || category;

            return (
              <div key={category} data-testid={`doc-category-${category}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CatIcon size={16} className={colorClass.split(' ')[0]} />
                  <h4 className="text-sm font-medium">{categoryLabel}</h4>
                  <Badge variant="secondary" className="text-xs">{docs.length}</Badge>
                </div>
                <div className="space-y-1.5 pl-6 border-l-2 border-border ml-2">
                  {docs.map((doc, i) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 bg-card border border-border rounded-lg p-2.5 -ml-[9px]"
                      data-testid={`doc-item-${category}-${i}`}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colorClass.split(' ')[0]} bg-current`} />
                      <FileText size={16} className="text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{doc.doc_name}</span>
                          <Badge variant="outline" className={`text-xs ${colorClass}`}>{doc.doc_type_name}</Badge>
                          <span className="text-xs text-muted-foreground">{formatSize(doc.size)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <span>{doc.uploaded_by_name}</span>
                          <span>{new Date(doc.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          {doc.visible_to_client ? (
                            <Badge variant="outline" className="text-green-500 border-green-500/30 text-xs py-0"><Eye size={8} className="mr-0.5" />Cliente</Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-400 border-red-400/30 text-xs py-0"><EyeOff size={8} className="mr-0.5" />Oculto</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                          <Download size={14} />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleToggleVisibility(doc)}>
                              {doc.visible_to_client ? <EyeOff size={14} /> : <Eye size={14} />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)}>
                              <Trash2 size={14} className="text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
